import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "@/lib/adminApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_66258ad9-e000-456c-bbb1-9b3da697cdba/artifacts/pl46n86d_New%20SCAH%20Logo.png";

const formatDetail = (detail) => {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (typeof detail === "object" && detail.msg) return detail.msg;
  return String(detail);
};

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/forgot-password`, { email });
      setSubmitted(true);
    } catch (error) {
      toast.error(formatDetail(error.response?.data?.detail) || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-900 flex items-center justify-center p-6"
      data-testid="forgot-password-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="SCAH Logo" className="h-20 w-auto mx-auto mb-4" />
          <h1 className="font-serif text-3xl font-bold text-white mb-2">Reset Password</h1>
          <p className="text-slate-400">Sickle Cell Association</p>
        </div>

        <Card className="border-slate-800 bg-slate-800/50">
          <CardContent className="p-8">
            {submitted ? (
              <div className="text-center" data-testid="forgot-password-success">
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-white text-xl font-semibold mb-2">Check your email</h2>
                <p className="text-slate-400 text-sm leading-relaxed">
                  If an admin account exists for <span className="text-slate-200">{email}</span>,
                  we&apos;ve sent a password reset link to that address. The link is valid for 1 hour.
                </p>
                <p className="text-slate-500 text-xs mt-4">
                  Didn&apos;t receive it? Check your spam folder, or contact the site administrator.
                </p>
                <Link
                  to="/admin/login"
                  className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm mt-6"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <p className="text-slate-400 text-sm mb-6">
                  Enter the email associated with your admin account and we&apos;ll send you a secure
                  link to choose a new password.
                </p>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="forgot-email" className="text-slate-300">
                      Email Address
                    </Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="forgot-email"
                        type="email"
                        placeholder="admin@sicklecellhouston.org"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 bg-slate-700 border-slate-600 text-white"
                        required
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 mt-2"
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? "Sending link..." : "Send Reset Link"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>

                  <div className="text-center pt-2">
                    <Link
                      to="/admin/login"
                      className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm"
                      data-testid="back-to-login-link"
                    >
                      <ArrowLeft className="w-4 h-4" /> Back to login
                    </Link>
                  </div>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          <a href="/" className="hover:text-slate-300">
            ← Back to Website
          </a>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
